import React, { useState, useEffect } from 'react';
import { Box, Button, Stack, TextField, IconButton, Typography } from '@mui/material';
import { Formik, Form, FieldArray, getIn } from 'formik';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Icon } from '@iconify/react';
import GlobalDialog from '../../../../components/mui/Dialogue/GlobalDialog';
import {
  PostCourseEnrollment,
  deleteSubjectEnrollmentSchedule,
  getAllsubjects,
  getAllsubjectsEnrollData,
  getCourseEnrollments,
  getCroLeadsAll,
  updatesessionstartdate,
} from '../../../../redux/features/croSlice';
import { savePaginationData } from '../../../../redux/features/globalDatagridSlice';
import dayjs from 'dayjs';
import { Autocomplete } from '@mui/material';
import Loader3D from '../../../../components/Loader/Loader3D';
import WarningPopup from './components/WarningPopup';
import GlobalDeleteDialog from '../../../../components/mui/Dialogue/GlobalDeleteDialog';

// canEnroll (optional): the retention workflow enrolls before Admission Confirmed (RETENTION_WORKFLOW.md §5.5 stage 3).
// When omitted, the old gate applies: only Admission Confirmed students can be enrolled.
// onEnrolled (optional): runs after a successful enrolment. renderTrigger (optional): (open) => button, replacing the
// default green button (the retention workspace uses its own theme).
const CourseEnrollment = ({
  courseenrollmentId,
  studentStatus,
  FeePersession,
  dispatchdata,
  canEnroll,
  onEnrolled,
  renderTrigger,
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [warning, setWarning] = useState(false);

  const { allsubjects, loading, CourseEnrollData } = useSelector((state) => state.cro);
  const { paginationData } = useSelector((state) => state.globalDataGrid);

  const initialValues = {
    subjects: [
      {
        subject_id: '',
        total_sessions: CourseEnrollData.total_sessions || '',
        cro_class_per_week: '',
        cro_fee_per_session: FeePersession || '',
        session_start_date: null,
        firstsession_starttime: null,
        firstsession_endtime: null,
      },
    ],
  };
  const [formInitialValues, setFormInitialValues] = useState(initialValues);

  const validationSchema = Yup.object().shape({
    subjects: Yup.array().of(
      Yup.object().shape({
        subject_id: Yup.string().required('Subject is required'),
        total_sessions: Yup.number().required('Total sessions required'),
        cro_class_per_week: Yup.number().required('Classes per week required'),
        cro_fee_per_session: Yup.number().required('Fee per session required'),
        session_start_date: Yup.string().nullable().required('Start date required'),
        firstsession_starttime: Yup.string().nullable().required('Start time required'),
        firstsession_endtime: Yup.string().nullable().required('End time required'),
      }),
    ),
  });

  const handleSubmit = (values) => {
    const newSubjects = values.subjects.filter((subj) => !subj.isPrefilled);

    if (newSubjects.length === 0) {
      enqueueSnackbar('No new subjects to enroll', { variant: 'warning' });
      return;
    }

    const payload = {
      course_enrollment_id: courseenrollmentId,
      subjects: newSubjects.map((subj) => ({
        ...subj,
        session_start_date: dayjs(subj.session_start_date).format('YYYY-MM-DD'),
        firstsession_starttime: dayjs(subj.firstsession_starttime).format('HH:mm:ss'),
        firstsession_endtime: dayjs(subj.firstsession_endtime).format('HH:mm:ss'),
      })),
    };

    dispatch(PostCourseEnrollment(payload)).then((res) => {
      if (res.payload.status === 'success') {
        setWarning(true);
        enqueueSnackbar(res.payload.message, { variant: 'success' });
        setOpen(false);
        if (dispatchdata) dispatch(getCroLeadsAll(dispatchdata));
        onEnrolled?.();
        dispatch(savePaginationData({ ...paginationData, openAnchor: false }));
      } else {
        enqueueSnackbar(res.payload.message, { variant: 'error' });
      }
    });
  };

  const handleWarningPopup = (values) => {
    setWarning(values);
  };

  useEffect(() => {
    if (open) {
      dispatch(getAllsubjects());
      dispatch(getAllsubjectsEnrollData(courseenrollmentId));
    }
  }, [open, refresh]);

  useEffect(() => {
    const hasPrefillData = Array.isArray(CourseEnrollData) && CourseEnrollData.length > 0;
    const hasSubjects = Array.isArray(allsubjects) && allsubjects.length > 0;

    if (hasPrefillData && hasSubjects) {
      const transformedSubjects = CourseEnrollData.map((subj) => ({
        subject_id: subj.subject_id || '',
        total_sessions: subj.total_sessions || '',
        subject_enrollment_id: subj.subject_enrollment_id, // 👈 ADD THIS
        cro_class_per_week: subj.cro_class_per_week || '',
        cro_fee_per_session: subj.cro_fee_per_session || '',
        session_start_date: subj.session_start_date ? dayjs(subj.session_start_date) : null,
        firstsession_starttime: subj.firstsession_starttime ? dayjs(`1970-01-01T${subj.firstsession_starttime}`) : null,
        firstsession_endtime: subj.firstsession_endtime ? dayjs(`1970-01-01T${subj.firstsession_endtime}`) : null,
        isPrefilled: true,
      }));

      setFormInitialValues({ subjects: transformedSubjects });
    } else if (!hasPrefillData) {
      setFormInitialValues(initialValues); // fallback for empty data
    }
  }, [CourseEnrollData, allsubjects]);

  return (
    <GlobalDialog
      title="Enroll Subject"
      open={open}
      setOpen={setOpen}
      closeButton={true}
      maxWidth={'md'}
      fullWidth
      popupButton={
        renderTrigger ? (
          renderTrigger(() => setOpen(true))
        ) : (
          <Button
            onClick={() => setOpen(true)}
            sx={{
              p: '7px 10px',
              gap: '5px',
              background: 'linear-gradient(to right,#7AC74F, #329F5B)',
              color: '#fff',
              '&:hover': {
                background: 'linear-gradient(to right,#59A96A, #3F612D)',
              },
            }}
            disabled={canEnroll === undefined ? studentStatus !== 'Admission Confirmed' : !canEnroll}
          >
            <Icon icon="bi:calendar2-date-fill" width="20" height="20" />
            Enroll Subject
          </Button>
        )
      }
      content={
        loading ? (
          <Loader3D />
        ) : (
          <Box>
            <GlobalDialog
              title={'Warning'}
              content={<WarningPopup handleSubmit={handleSubmit} values={warning} />}
              setOpen={setWarning}
              open={Boolean(warning)}
              closeButton
            />
            <Formik
              initialValues={formInitialValues}
              enableReinitialize={true}
              validationSchema={validationSchema}
              onSubmit={handleWarningPopup}
            >
              {({ values, errors, touched, handleChange, setFieldValue, handleBlur }) => (
                <Form>
                  <FieldArray name="subjects">
                    {({ push, remove }) => (
                      <>
                        {values.subjects.map((subj, index) => {
                          const field = (name) => `subjects[${index}].${name}`;
                          const error = (name) => getIn(touched, field(name)) && getIn(errors, field(name));

                          return (
                            <Box
                              key={index}
                              sx={{
                                mb: 4,
                                borderBottom: '1px solid #ccc',
                                pb: 2,
                              }}
                            >
                              <Stack spacing={2}>
                                <Typography variant="h6">Subject {index + 1}</Typography>

                                <Autocomplete
                                  options={allsubjects}
                                  getOptionLabel={(option) => option.subject_name || ''}
                                  isOptionEqualToValue={(option, value) => option.subject_id === value?.subject_id}
                                  value={allsubjects.find((option) => option.subject_id === subj.subject_id) || null}
                                  onChange={(e, value) =>
                                    setFieldValue(`subjects[${index}].subject_id`, value?.subject_id || '')
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Subject"
                                      error={!!error('subject_id')}
                                      helperText={error('subject_id')}
                                    />
                                  )}
                                />

                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                      label="Start Date"
                                      value={subj.session_start_date}
                                      onChange={(val) => setFieldValue(field('session_start_date'), val)}
                                      slotProps={{
                                        textField: {
                                          fullWidth: true,
                                          error: !!error('session_start_date'),
                                          helperText: error('session_start_date'),
                                        },
                                      }}
                                      minDate={dayjs()}
                                    />
                                    <TimePicker
                                      label="Start Time"
                                      value={subj.firstsession_starttime}
                                      onChange={(val) => setFieldValue(field('firstsession_starttime'), val)}
                                      slotProps={{
                                        textField: {
                                          fullWidth: true,
                                          error: !!error('firstsession_starttime'),
                                          helperText: error('firstsession_starttime'),
                                        },
                                      }}
                                    />
                                    <TimePicker
                                      label="End Time"
                                      value={subj.firstsession_endtime}
                                      onChange={(val) => setFieldValue(field('firstsession_endtime'), val)}
                                      slotProps={{
                                        textField: {
                                          fullWidth: true,
                                          error: !!error('firstsession_endtime'),
                                          helperText: error('firstsession_endtime'),
                                        },
                                      }}
                                    />
                                  </LocalizationProvider>
                                </Stack>

                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                  <TextField
                                    fullWidth
                                    label="Total Sessions"
                                    name={field('total_sessions')}
                                    value={subj.total_sessions}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!error('total_sessions')}
                                    helperText={error('total_sessions')}
                                  />
                                  <TextField
                                    fullWidth
                                    label="Classes per Week"
                                    name={field('cro_class_per_week')}
                                    value={subj.cro_class_per_week}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!error('cro_class_per_week')}
                                    helperText={error('cro_class_per_week')}
                                  />
                                  <TextField
                                    fullWidth
                                    label="Fee per Session"
                                    name={field('cro_fee_per_session')}
                                    value={subj.cro_fee_per_session}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!error('cro_fee_per_session')}
                                    helperText={error('cro_fee_per_session')}
                                  />
                                </Stack>

                                <Box display="flex" justifyContent="center" gap={2}>
                                  {!subj.isPrefilled && values.subjects.length > 1 && (
                                    <Button variant="outlined" color="error" onClick={() => remove(index)}>
                                      <Icon icon="mdi:trash-can" width={20} height={20} />
                                      Remove
                                    </Button>
                                  )}
                                  {subj.isPrefilled && (
                                    <GlobalDeleteDialog
                                      api={deleteSubjectEnrollmentSchedule({
                                        subject_enrollment_id: values.subjects?.[index]?.subject_enrollment_id,
                                      })}
                                      deleteNote={'Are You Sure To Delete ?'}
                                      upDateCall={getAllsubjectsEnrollData(courseenrollmentId)}
                                    />
                                  )}

                                  {subj.isPrefilled && (
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      onClick={() => {
                                        const originalSubjectData = CourseEnrollData.find(
                                          (item) => item.subject_enrollment_id === subj.subject_enrollment_id,
                                        );

                                        const payload = {
                                          course_enrollment_id: courseenrollmentId,
                                          subject_enrollment_id: originalSubjectData?.subject_enrollment_id || '',
                                          subject_id: subj.subject_id,
                                          total_sessions: subj.total_sessions,
                                          cro_class_per_week: subj.cro_class_per_week,
                                          cro_fee_per_session: subj.cro_fee_per_session,
                                          session_start_date: dayjs(subj.session_start_date).format('YYYY-MM-DD'),
                                          firstsession_starttime: dayjs(subj.firstsession_starttime).format('HH:mm:ss'),
                                          firstsession_endtime: dayjs(subj.firstsession_endtime).format('HH:mm:ss'),
                                        };

                                        dispatch(updatesessionstartdate(payload)).then((res) => {
                                          if (res.payload.status === 'success') {
                                            enqueueSnackbar('Session updated successfully!', {
                                              variant: 'success',
                                            });
                                            setOpen(false);
                                          } else {
                                            enqueueSnackbar(res.payload.message || 'Failed to update session', {
                                              variant: 'error',
                                            });
                                          }
                                        });
                                      }}
                                    >
                                      Update
                                    </Button>
                                  )}
                                </Box>
                              </Stack>
                            </Box>
                          );
                        })}
                        <Box mb={2} sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Button
                            variant="outlined"
                            startIcon={<Icon icon="mdi:plus" />}
                            onClick={() =>
                              push({
                                subject_id: '',
                                total_sessions: '',
                                cro_class_per_week: '',
                                cro_fee_per_session: '',
                                session_start_date: null,
                                firstsession_starttime: null,
                                firstsession_endtime: null,
                                isPrefilled: false,
                              })
                            }
                          >
                            Add Subject Enroll
                          </Button>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button variant="outlined" onClick={() => setOpen(false)}>
                            Cancel
                          </Button>
                          <Button type="submit" variant="contained" disabled={loading} sx={{ ml: 2 }}>
                            {loading ? 'Please wait...' : 'Create New Enrollment'}
                          </Button>
                        </Box>
                      </>
                    )}
                  </FieldArray>
                </Form>
              )}
            </Formik>
          </Box>
        )
      }
    />
  );
};

export default CourseEnrollment;
